import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { MessageCircle } from "lucide-react";
import { api, apiError } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";

const StatusBadge = ({ s }) => (
  <Badge className={`border capitalize ${s === "approved" ? "status-approved" : s === "rejected" ? "status-rejected" : "status-pending"}`}>
    {s}
  </Badge>
);

export default function ReaderDashboard() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [apps, setApps] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/applications/mine").then(({ data }) => setApps(data)).catch((e) => toast.error(apiError(e))).finally(() => setLoading(false));
  }, []);

  const messageAuthor = async (authorId) => {
    try {
      const { data } = await api.post("/conversations", { other_user_id: authorId });
      nav(`/messages?c=${data.id}`);
    } catch (e) { toast.error(apiError(e)); }
  };

  const stats = {
    total: apps.length,
    pending: apps.filter((a) => a.status === "pending").length,
    approved: apps.filter((a) => a.status === "approved").length,
    rejected: apps.filter((a) => a.status === "rejected").length,
  };

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-10">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-8">
        <div>
          <div className="overline mb-2">Reader Dashboard</div>
          <h1 className="font-serif text-4xl tracking-tight">Hello, {user?.display_name}</h1>
          <p className="text-slate-600 mt-1 text-sm">Track your ARC applications and update your reviewer profile.</p>
        </div>
        <div className="flex gap-2">
          <Link to="/profile"><Button variant="outline" className="btn-press" data-testid="reader-profile-btn">Edit profile</Button></Link>
          <Link to="/campaigns"><Button className="btn-press bg-[color:var(--oxford)] hover:bg-[color:var(--oxford-hover)] text-white" data-testid="reader-browse-btn">Browse campaigns</Button></Link>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10">
        {[
          { k: "total", label: "Applications" },
          { k: "pending", label: "Pending" },
          { k: "approved", label: "Approved" },
          { k: "rejected", label: "Declined" },
        ].map((s) => (
          <div key={s.k} className="paper-card p-5" data-testid={`stat-${s.k}`}>
            <div className="overline mb-1">{s.label}</div>
            <div className="font-serif text-3xl">{stats[s.k]}</div>
          </div>
        ))}
      </div>

      <div className="overline mb-3">My ARC Applications</div>
      {loading ? (
        <div className="text-slate-500">Loading…</div>
      ) : apps.length === 0 ? (
        <div className="paper-card p-10 text-center">
          <div className="font-serif text-2xl mb-2">No applications yet.</div>
          <p className="text-sm text-slate-600 mb-4">Browse open campaigns and apply to your first ARC.</p>
          <Link to="/campaigns"><Button className="btn-press bg-[color:var(--oxford)] hover:bg-[color:var(--oxford-hover)] text-white">Browse campaigns</Button></Link>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-3">
          {apps.map((a) => (
            <div key={a.id} className="paper-card p-4 flex flex-col md:flex-row md:items-center gap-4" data-testid={`app-row-${a.id}`}>
              <img
                src={a.campaign?.cover_url || "https://images.unsplash.com/photo-1620233977581-509e4c6b0636?crop=entropy&cs=srgb&fm=jpg&q=85&w=400"}
                alt=""
                className="w-16 h-20 rounded object-cover flex-shrink-0"
              />
              <div className="flex-1">
                <Link to={`/campaigns/${a.campaign_id}`} className="font-serif text-lg hover:underline">{a.campaign?.title}</Link>
                <div className="text-sm text-slate-600">by {a.campaign?.pen_name} · Format: <span className="capitalize">{a.preferred_format}</span></div>
                {a.comment && <div className="text-xs text-slate-500 mt-1 italic line-clamp-1">"{a.comment}"</div>}
              </div>
              <div className="flex items-center gap-2">
                {a.auto_accepted && <Badge className="status-auto border">Auto</Badge>}
                <StatusBadge s={a.status} />
                <Button size="sm" variant="outline" className="btn-press" onClick={() => messageAuthor(a.campaign?.author_id)} data-testid={`message-author-${a.id}`}>
                  <MessageCircle size={14} className="mr-1" /> Message
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
